import { useMutation } from '@tanstack/react-query';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ResultadoArchivo } from '@manejo-alcanza/contracts';
import { importar, pesos } from '../api';
import { Aviso, Card, Error_ } from '../componentes/basicos';

/**
 * «Cargar órdenes»: una caja donde se sueltan archivos. Nada más.
 *
 * Si cargar una orden cuesta más trabajo que reenviar el PDF por WhatsApp,
 * no se va a usar. Por eso no hay formulario: el archivo trae los datos.
 */
export function CargarOrdenes(): ReactNode {
  const [arrastrando, setArrastrando] = useState(false);
  const [cuerpoCorreo, setCuerpoCorreo] = useState('');
  const entrada = useRef<HTMLInputElement>(null);

  const carga = useMutation({
    mutationFn: (archivos: File[]) => importar(archivos, cuerpoCorreo),
  });

  function soltar(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setArrastrando(false);
    const archivos = [...e.dataTransfer.files];
    if (archivos.length > 0) carga.mutate(archivos);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Cargar órdenes</h1>
        <p className="mt-1 text-tenue">
          Suelta aquí los archivos que descargaste del CEN. El sistema reconoce el cliente solo.
        </p>
      </header>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={soltar}
        onClick={() => entrada.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition ${
          arrastrando ? 'border-acento bg-acento-tenue' : 'border-borde bg-superficie'
        }`}
      >
        <p className="text-lg font-medium">Suelta los archivos aquí</p>
        <p className="mt-1 text-tenue">
          PDF o JSON, varios a la vez. Con el PDF basta: es el que descargas del portal.
        </p>
        <input
          ref={entrada}
          type="file"
          multiple
          accept=".json,.pdf,application/json,application/pdf"
          className="hidden"
          onChange={(e) => {
            const archivos = [...(e.target.files ?? [])];
            if (archivos.length > 0) carga.mutate(archivos);
            e.target.value = '';
          }}
        />
      </div>

      {/*
        El cuerpo del correo solo aplica al canal correo, y es opcional. Va
        debajo de la caja y no encima para no estorbar el caso normal, que es
        soltar un archivo del CEN y nada mas.
      */}
      <details className="rounded-lg border border-borde bg-superficie p-3">
        <summary className="cursor-pointer text-sm font-medium">
          ¿La orden llegó por correo? Pega aquí el mensaje
        </summary>
        <p className="mt-2 text-xs text-tenue">
          Opcional, y solo para las órdenes que llegan por correo. Vale la pena: el cuerpo del
          mensaje trae <strong>la ventana horaria de entrega</strong>, que el PDF no tiene, y el
          importe total con el que se contrasta el del adjunto.
        </p>
        <textarea
          value={cuerpoCorreo}
          onChange={(e) => setCuerpoCorreo(e.target.value)}
          rows={6}
          placeholder="Pega aquí el cuerpo del correo, tal cual llegó"
          className="mt-2 w-full rounded-md border border-borde px-2 py-1.5 font-mono text-xs"
        />
      </details>

      {carga.isPending && <Aviso>Procesando los archivos…</Aviso>}
      {carga.isError && <Error_>{(carga.error as Error).message}</Error_>}

      {carga.data && <ResultadoCarga resultados={carga.data.resultados} />}
    </div>
  );
}

/**
 * El resultado en tres grupos: qué se entendió, qué quedó por revisar con el
 * detalle, y qué ya existía.
 */
function ResultadoCarga({ resultados }: { resultados: ResultadoArchivo[] }): ReactNode {
  const bien = resultados.filter((r) => r.desenlace === 'IMPORTADA' || r.desenlace === 'PDF_ADJUNTADO');
  const revisar = resultados.filter((r) => r.desenlace === 'IMPORTADA_CON_INCIDENCIAS');
  const yaEstaban = resultados.filter(
    (r) => r.desenlace === 'YA_EXISTIA' || r.desenlace === 'ARCHIVO_REPETIDO',
  );
  const rechazados = resultados.filter((r) => r.desenlace === 'RECHAZADA');

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Resultado de la última carga</h2>

      {bien.length > 0 && (
        <Card titulo={`Se entendieron completas (${bien.length})`}>
          <ul className="space-y-2">
            {bien.map((r, i) => (
              <li key={i} className="flex flex-wrap items-baseline justify-between gap-2">
                {r.desenlace === 'IMPORTADA' ? (
                  <>
                    <Link to={`/ordenes/${r.ordenId}`} className="font-medium text-acento hover:underline">
                      {r.numero}
                    </Link>
                    <span className="text-tenue">
                      {r.clienteNombre} · {r.lineas} líneas · {r.destinosPredistribucion} destinos
                    </span>
                    <span className="cifra font-medium">{pesos(r.valorPedido)}</span>
                  </>
                ) : r.desenlace === 'PDF_ADJUNTADO' ? (
                  <>
                    <Link to={`/ordenes/${r.ordenId}`} className="font-medium text-acento hover:underline">
                      {r.numero}
                    </Link>
                    <span className="text-tenue">
                      PDF adjuntado ·{' '}
                      {r.coincideConCalculado === null
                        ? 'sin total legible'
                        : r.coincideConCalculado
                          ? 'el total impreso cuadra'
                          : 'el total impreso NO cuadra'}
                    </span>
                    <span className="cifra">{pesos(r.totalPdf)}</span>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {revisar.length > 0 && (
        <Card titulo={`Quedaron por revisar (${revisar.length})`}>
          <ul className="space-y-3">
            {revisar.map((r, i) => {
              if (r.desenlace !== 'IMPORTADA_CON_INCIDENCIAS') return null;
              const porCodigo = new Map<string, number>();
              for (const inc of r.incidencias) {
                porCodigo.set(inc.codigo, (porCodigo.get(inc.codigo) ?? 0) + 1);
              }
              return (
                <li key={i} className="border-l-2 border-aviso pl-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <Link to={`/ordenes/${r.ordenId}`} className="font-medium text-acento hover:underline">
                      {r.numero}
                    </Link>
                    <span className="text-tenue">{r.clienteNombre}</span>
                    <span className="cifra font-medium">{pesos(r.valorPedido)}</span>
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-tenue">
                    {[...porCodigo].map(([codigo, n]) => (
                      <li key={codigo}>
                        {codigo.replaceAll('_', ' ').toLowerCase()} · {n}{' '}
                        {n === 1 ? 'vez' : 'veces'}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {yaEstaban.length > 0 && (
        <Card titulo={`Ya estaban cargadas (${yaEstaban.length})`}>
          <ul className="space-y-1 text-sm">
            {yaEstaban.map((r, i) => (
              <li key={i} className="flex items-baseline justify-between gap-2">
                <span className="text-tenue">
                  {r.desenlace === 'ARCHIVO_REPETIDO'
                    ? 'El mismo archivo, ya cargado antes'
                    : 'Ya existía una orden con ese número'}
                </span>
                {r.ordenId ? (
                  <Link to={`/ordenes/${r.ordenId}`} className="font-medium text-acento hover:underline">
                    {r.numero} {r.desenlace === 'YA_EXISTIA' ? `· ${r.estado}` : ''}
                  </Link>
                ) : (
                  <span className="text-tenue">{r.nombreArchivo}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {rechazados.length > 0 && (
        <Card titulo={`No se pudieron leer (${rechazados.length})`}>
          <ul className="space-y-2 text-sm">
            {rechazados.map((r, i) => (
              <li key={i}>
                {r.desenlace === 'RECHAZADA' && (
                  <>
                    <div className="font-mono text-xs">{r.nombreArchivo}</div>
                    <div className="text-tenue">{r.motivo}</div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
